/**
 * Pure viewport-aware placement for an anchored popover: below the anchor
 * when it fits, flipped above when it does not, and always clamped inside
 * the bounds — so a preview opened from the last month column or the bottom
 * of the day grid is never cut off.
 */
export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

export type PopoverPlacement = 'below' | 'above';

export interface PlacedPopover {
  top: number;
  left: number;
  placement: PopoverPlacement;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

export function placePopover(
  anchor: Rect,
  popover: Size,
  bounds: Size,
  gap: number,
  margin: number,
): PlacedPopover {
  const maxTop = bounds.height - margin - popover.height;
  const below = anchor.y + anchor.height + gap;
  const above = anchor.y - gap - popover.height;

  let top: number;
  let placement: PopoverPlacement;
  if (below <= maxTop) {
    top = below;
    placement = 'below';
  } else if (above >= margin) {
    top = above;
    placement = 'above';
  } else {
    top = clamp(below, margin, Math.max(margin, maxTop));
    placement = 'below';
  }

  const maxLeft = bounds.width - margin - popover.width;
  const left = clamp(anchor.x, margin, Math.max(margin, maxLeft));

  return { top, left, placement };
}
